import React from "react"
import { Box, Flex, Text } from "@chakra-ui/react"
import type { KioskHomeLayout } from "../kioskLayout"
import KioskPreviewHomeActionButton from "./KioskPreviewHomeActionButton"

type Props = {
  layout: KioskHomeLayout
  accent: string
  title: string
  /** Fitted title font size (px) — measured once by the device wrapper. */
  titleFontPx: number
  scanLabel: string
  registerLabel: string
  scanQrCodeEnabled: boolean
  walkInRegistrationEnabled: boolean
  logoUrl: string
}

/**
 * Pre-scan screen: a brand block (logo + optional title) above a centered button row.
 * All positions are absolute px from the spec layout, measured from the screen-region
 * top-left (which already accounts for the status-bar inset).
 */
const KioskPreviewHome = ({
  layout,
  accent,
  title,
  titleFontPx,
  scanLabel,
  registerLabel,
  scanQrCodeEnabled,
  walkInRegistrationEnabled,
  logoUrl,
}: Props) => {
  const { brand, buttons } = layout

  return (
    <Box position="absolute" inset={0} color="white">
      {(brand.hasLogo || brand.hasTitle) && (
        <Box
          position="absolute"
          top={`${brand.top}px`}
          left={`${brand.left}px`}
          w={`${brand.width}px`}
          h={`${brand.height}px`}
        >
          {brand.hasLogo && logoUrl && (
            <Box
              as="img"
              src={logoUrl}
              alt="Brand Logo"
              position="absolute"
              top={0}
              left={0}
              w="100%"
              h={`${brand.logoHeight}px`}
              objectFit="contain"
            />
          )}
          {brand.hasTitle && (
            <Flex
              position="absolute"
              top={`${brand.titleTop}px`}
              left={0}
              w="100%"
              h={`${brand.titleHeight}px`}
              // With a logo, top-anchor the title so a 2-line title keeps a constant gap
              // below the logo (the 2nd line grows downward); centered when logo-less.
              align={brand.hasLogo ? "flex-start" : "center"}
              justify="center"
            >
              <Text
                fontSize={`${titleFontPx}px`}
                fontWeight="medium"
                lineHeight={1.05}
                textAlign="center"
                noOfLines={2}
                // Balance the two lines (e.g. "Netflix Cannes" / "Lions 2026") instead of a
                // greedy break, matching the iPad regardless of the exact band width.
                sx={{ textWrap: "balance" }}
                textShadow="0 2px 14px rgba(0,0,0,0.35)"
              >
                {title}
              </Text>
            </Flex>
          )}
        </Box>
      )}

      {buttons.height > 0 && (
        <Flex
          position="absolute"
          top={`${buttons.top}px`}
          left={0}
          w="100%"
          h={`${buttons.height}px`}
          align="center"
          justify="center"
          gap={`${buttons.gap}px`}
        >
          {scanQrCodeEnabled && (
            <KioskPreviewHomeActionButton
              accent={accent}
              label={scanLabel}
              icon="email-block-qr-code"
              layout={buttons}
              variant="scan"
            />
          )}
          {walkInRegistrationEnabled && (
            <KioskPreviewHomeActionButton
              accent={accent}
              label={registerLabel}
              icon="keyboard-icon"
              layout={buttons}
              variant="register"
            />
          )}
        </Flex>
      )}
    </Box>
  )
}

export default KioskPreviewHome
